import React, { Fragment, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import ForwardIcon from "@material-ui/icons/ChevronRight";
import Occasion from "../occasion";
import { getFilteredOccasions, getAllFilters } from "../Filter/util";
import { AppContext, SET_OCCASIONFILTER } from "../Context";
import { STATE_CULTURE } from "../Actions/stateActions";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "1024px",
    margin: "0 auto",
    color: theme.app.color,
    "@media(max-width: 1300px)": {
      width: "77%",
    },
    "@media(max-width: 1024px)": {
      width: "100%",
    },
  },
  heading: {
    textTransform: "capitalize",
    color: theme.app.labelColor,
    margin: "24px 0 8px",
    lineHeight: 1.2,
    "@media(max-width: 768px)": {
      margin: "16px 0 8px",
      padding: "0 8px 2px",
    },
    "@media(max-width: 600px)": {
      marginTop: 0,
      padding: "0 8px",
      "&:first-child": { paddingTop: "8px" },
    },
  },
  filterContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "1rem",
  },
  list: { padding: 0 },
  noBookable: {
    textAlign: "center",
  },
  noBookableButtonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "16px",
  },
  buttonText: {
    "&:after": {
      content: "' '",
      whiteSpace: "pre",
    },
  },
  chip: {
    margin: "4px",
    "& svg": {
      color: theme.app.badge.color,
    },
  },
}));

const reapplyActiveFilters = (
  activeFilters,
  setFilter,
  setIsFiltersReapplied,
  occasionFilter
) => {
  const { locations, activities, weekdays, tags } = activeFilters;

  if (locations) {
    locations.forEach((locationName) => {
      if (!occasionFilter.locations.includes(locationName)) {
        setFilter("locations", locationName, true);
      }
    });
  }
  if (activities) {
    activities.forEach((activityName) => {
      if (!occasionFilter.activities.includes(activityName)) {
        setFilter("activities", activityName, true);
      }
    });
  }
  if (weekdays) {
    weekdays.forEach((weekDay) => {
      if (!occasionFilter.weekdays.includes(weekDay)) {
        setFilter("weekdays", weekDay, true);
      }
    });
  }
  if (tags) {
    tags.forEach((tagName) => {
      if (!occasionFilter.tags.includes(tagName)) {
        setFilter("tags", tagName, true);
      }
    });
  }
  setIsFiltersReapplied(true);
};

const OccasionsList = (props) => {
  const { manages, week, occasions } = props;
  const classes = useStyles();
  const [isFiltersReapplied, setIsFiltersReapplied] = useState(false);
  const { language, culture } = useSelector((state) => ({
    language: state.language,
    culture: state.cookies[STATE_CULTURE],
  }));
  const context = useContext(AppContext);
  const { dispatch, state } = context;
  const { occasionFilter } = state;
  const filteredOccasions = getFilteredOccasions(
    occasions,
    occasionFilter,
    culture
  );
  const filterChips = getAllFilters(occasionFilter);
  const locale = culture.substring(0, 2).toLowerCase();
  const removeFilter = (type, value, checked) =>
    dispatch({ type: SET_OCCASIONFILTER, payload: { type, value, checked } });

  const setFilter = React.useCallback(
    (type, value, checked) => {
      dispatch({ type: SET_OCCASIONFILTER, payload: { type, value, checked } });
    },
    [dispatch]
  );

  useEffect(() => {
    const activeFilters = JSON.parse(localStorage.getItem("ActiveFilters"));
    if (activeFilters && isFiltersReapplied === false) {
      reapplyActiveFilters(
        activeFilters,
        setFilter,
        setIsFiltersReapplied,
        occasionFilter
      );
    }
  }, [occasionFilter, isFiltersReapplied, setFilter]);

  return (
    <div className={classes.container}>
      <div className={classes.filterContainer}>
        {filterChips.map((filter, i) => {
          return (
            <Chip
              key={i}
              label={filter.label}
              onClick={() => removeFilter(filter.type, filter.value, false)}
              onDelete={() => removeFilter(filter.type, filter.value, false)}
              color="primary"
              className={classes.chip}
              aria-label={`${language.removeFilter} ${filter.label}`}
            />
          );
        })}
      </div>
      <div>
        {filteredOccasions.map((o, i) => {
          return (
            <Fragment key={i}>
              <Typography variant="h2" className={classes.heading}>
                {moment(o.occasions[0].at)
                  .locale(locale)
                  .format("dddd DD MMM YYYY")}
              </Typography>
              <List className={classes.list}>
                {o.occasions.map((occ, i) => {
                  return (
                    <Occasion
                      key={occ.id}
                      occasion={occ}
                      language={language}
                      culture={culture}
                      showTooltip={true}
                      manages={manages}
                      index={i}
                    />
                  );
                })}
              </List>
            </Fragment>
          );
        })}
      </div>
      {filteredOccasions.length <= 0 ? (
        <div>
          <Typography className={classes.noBookable} variant="h3">
            {`${language.nothing_bookable} - ${language.week} ${week.thisWeek}`}
          </Typography>
          <div className={classes.noBookableButtonContainer}>
            <Button
              variant="outlined"
              component={Link}
              to={`/week/${week.nextYear}/${week.nextWeek}`}
              size="small"
            >
              <span className={classes.buttonText}>{language.next_week}</span>
              <ForwardIcon />
            </Button>
          </div>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
};

export default OccasionsList;
